<template>
  <CompanyAppLayout
    page-title="Contractor Payroll"
    page-subtitle="1099 contractor payments — day rate, per diem, mileage"
  >
    <div class="space-y-6 pt-4">
      <div class="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 class="text-2xl font-bold text-white">Contractor Payroll (1099)</h1>
          <p class="text-slate-400 text-sm mt-1">
            No tax withholding — contractors handle self-employment tax. Separate from W-2 payroll.
          </p>
        </div>
        <button class="btn-primary" @click="openPayModal()">
          + New Contractor Payment
        </button>
      </div>

      <p v-if="error" class="text-red-400">{{ error }}</p>

      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div class="stat-card">
          <p class="text-sm text-slate-400">Active Contractors</p>
          <p class="text-3xl font-bold text-white mt-1">{{ stats.activeContractors }}</p>
        </div>
        <div class="stat-card">
          <p class="text-sm text-slate-400">Total Paid / Pending</p>
          <p class="text-3xl font-bold text-white mt-1">${{ Number(stats.totalPayroll || 0).toLocaleString() }}</p>
        </div>
        <div class="stat-card">
          <p class="text-sm text-slate-400">Pending</p>
          <p class="text-3xl font-bold text-amber-400 mt-1">{{ stats.pendingPayments }}</p>
        </div>
        <div class="stat-card">
          <p class="text-sm text-slate-400">YTD Gross (1099)</p>
          <p class="text-3xl font-bold text-emerald-400 mt-1">${{ Number(stats.ytdGross || 0).toLocaleString() }}</p>
        </div>
      </div>

      <div class="panel">
        <div class="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 class="text-lg font-semibold text-white">Contractors</h2>
          <input v-model="search" type="text" placeholder="Search contractors…" class="form-input w-56" />
        </div>

        <div v-if="loading" class="text-slate-400 py-8 text-center">Loading…</div>
        <div v-else-if="!filteredContractors.length" class="text-slate-400 py-10 text-center">
          No contractors found. Hire crew or register contractor accounts to pay them here.
        </div>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="text-slate-400 border-b border-slate-700">
              <tr>
                <th class="py-2 pr-3 text-left font-medium">Contractor</th>
                <th class="py-2 pr-3 text-left font-medium">Role</th>
                <th class="py-2 pr-3 text-left font-medium">Day Rate</th>
                <th class="py-2 pr-3 text-left font-medium">Last Days</th>
                <th class="py-2 pr-3 text-left font-medium">YTD Gross</th>
                <th class="py-2 pr-3 text-left font-medium">Last Status</th>
                <th class="py-2 pr-3 text-left font-medium"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in filteredContractors" :key="c.id" class="border-b border-slate-800/80 text-slate-200">
                <td class="py-3 pr-3">
                  <div class="font-medium text-white">{{ c.name }}</div>
                  <div class="text-xs text-slate-500">{{ c.email }}</div>
                </td>
                <td class="py-3 pr-3">{{ c.role || 'Contractor' }}</td>
                <td class="py-3 pr-3">${{ Number(c.dayRate || 0).toLocaleString() }}/day</td>
                <td class="py-3 pr-3">{{ c.daysWorked || 0 }}</td>
                <td class="py-3 pr-3 text-emerald-400">${{ Number(c.ytdGross || 0).toLocaleString() }}</td>
                <td class="py-3 pr-3">
                  <span class="status-pill" :class="statusClass(c.status)">{{ c.status }}</span>
                </td>
                <td class="py-3 pr-3">
                  <button class="text-emerald-400 hover:text-emerald-300 font-medium" @click="openPayModal(c)">
                    Pay
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="panel">
        <h2 class="text-lg font-semibold text-white mb-4">Recent 1099 Payments</h2>
        <div v-if="!history.length" class="text-slate-400 py-8 text-center">No contractor payments yet.</div>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="text-slate-400 border-b border-slate-700">
              <tr>
                <th class="py-2 pr-3 text-left font-medium">Date</th>
                <th class="py-2 pr-3 text-left font-medium">Contractor</th>
                <th class="py-2 pr-3 text-left font-medium">Period</th>
                <th class="py-2 pr-3 text-left font-medium">Days × Rate</th>
                <th class="py-2 pr-3 text-left font-medium">Gross</th>
                <th class="py-2 pr-3 text-left font-medium">Status</th>
                <th class="py-2 pr-3 text-left font-medium">Ref</th>
                <th class="py-2 pr-3 text-left font-medium"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in history" :key="p.id" class="border-b border-slate-800/80 text-slate-200">
                <td class="py-3 pr-3">{{ p.date }}</td>
                <td class="py-3 pr-3 text-white">{{ p.contractor }}</td>
                <td class="py-3 pr-3">{{ p.periodStart }} → {{ p.periodEnd }}</td>
                <td class="py-3 pr-3">{{ p.daysWorked }} × ${{ Number(p.dayRate || 0).toLocaleString() }}</td>
                <td class="py-3 pr-3 text-emerald-400">${{ Number(p.amount || 0).toLocaleString() }}</td>
                <td class="py-3 pr-3">
                  <span class="status-pill" :class="statusClass(p.status)">{{ p.status }}</span>
                </td>
                <td class="py-3 pr-3 text-slate-500">{{ p.reference }}</td>
                <td class="py-3 pr-3">
                  <button
                    v-if="!['paid', 'cancelled'].includes(p.status)"
                    class="text-cyan-400 hover:text-cyan-300"
                    @click="markPaid(p)"
                  >
                    Mark Paid
                  </button>
                  <button
                    v-if="!['paid', 'cancelled'].includes(p.status)"
                    class="ml-3 text-red-400 hover:text-red-300"
                    @click="voidPayment(p)"
                  >Void</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-backdrop" @click.self="showModal = false">
      <form class="modal" @submit.prevent="submitPayment">
        <div class="flex items-start justify-between mb-4">
          <div>
            <p class="text-xs uppercase tracking-wide text-emerald-400/80">1099 Payment</p>
            <h3 class="text-xl font-semibold text-white">{{ form.contractorName || 'New Contractor Payment' }}</h3>
          </div>
          <button type="button" class="text-slate-400 text-2xl leading-none" @click="showModal = false">×</button>
        </div>

        <label class="field">
          <span>Contractor</span>
          <select v-model="form.contractorId" required class="form-input" @change="onContractorChange">
            <option disabled value="">Select contractor</option>
            <option v-for="c in contractors" :key="c.id" :value="String(c.id)">{{ c.name }}</option>
          </select>
        </label>

        <div class="grid grid-cols-2 gap-3">
          <label class="field">
            <span>Period Start</span>
            <input v-model="form.payPeriodStart" type="date" required class="form-input" />
          </label>
          <label class="field">
            <span>Period End</span>
            <input v-model="form.payPeriodEnd" type="date" required class="form-input" />
          </label>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <label class="field">
            <span>Pay Date</span>
            <input v-model="form.payDate" type="date" required class="form-input" />
          </label>
          <label class="field">
            <span>Day Rate ($)</span>
            <input v-model.number="form.dayRate" type="number" min="0" step="0.01" required class="form-input" />
          </label>
        </div>

        <div class="grid grid-cols-3 gap-3">
          <label class="field">
            <span>Days Worked</span>
            <input v-model.number="form.daysWorked" type="number" min="0" step="0.5" required class="form-input" />
          </label>
          <label class="field">
            <span>Per Diem $/day</span>
            <input v-model.number="form.perDiem" type="number" min="0" step="0.01" class="form-input" />
          </label>
          <label class="field">
            <span>Mileage</span>
            <input v-model.number="form.mileage" type="number" min="0" step="0.1" class="form-input" />
          </label>
        </div>

        <label class="field">
          <span>Other Expenses ($)</span>
          <input v-model.number="form.otherExpenses" type="number" min="0" step="0.01" class="form-input" />
        </label>

        <label class="field">
          <span>Notes</span>
          <textarea v-model="form.notes" rows="2" class="form-input" placeholder="Optional"></textarea>
        </label>

        <div class="calc-box">
          <div class="flex justify-between text-sm"><span>Base (days × rate)</span><span>${{ basePay.toFixed(2) }}</span></div>
          <div class="flex justify-between text-sm"><span>Per diem</span><span>${{ perDiemTotal.toFixed(2) }}</span></div>
          <div class="flex justify-between text-sm"><span>Mileage @ $0.67</span><span>${{ mileageTotal.toFixed(2) }}</span></div>
          <div class="flex justify-between text-sm"><span>Other</span><span>${{ Number(form.otherExpenses || 0).toFixed(2) }}</span></div>
          <div class="flex justify-between text-base font-bold text-emerald-400 border-t border-slate-600 pt-2 mt-2">
            <span>Gross / Net (no withholding)</span>
            <span>${{ previewGross.toFixed(2) }}</span>
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-4">
          <button type="button" class="btn-secondary" @click="showModal = false">Cancel</button>
          <button type="submit" class="btn-primary" :disabled="saving">
            {{ saving ? 'Saving…' : 'Create Payment' }}
          </button>
        </div>
      </form>
    </div>
  </CompanyAppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'

const contractors = ref<any[]>([])
const history = ref<any[]>([])
const stats = ref({
  activeContractors: 0,
  totalPayroll: 0,
  pendingPayments: 0,
  processedPayments: 0,
  ytdGross: 0,
})
const loading = ref(false)
const saving = ref(false)
const error = ref('')
const search = ref('')
const showModal = ref(false)

const form = reactive({
  contractorId: '',
  contractorName: '',
  payPeriodStart: '',
  payPeriodEnd: '',
  payDate: '',
  dayRate: 550,
  daysWorked: 7,
  perDiem: 0,
  mileage: 0,
  otherExpenses: 0,
  notes: '',
})

const filteredContractors = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return contractors.value
  return contractors.value.filter((c) =>
    `${c.name} ${c.email} ${c.role}`.toLowerCase().includes(q),
  )
})

const basePay = computed(() => Number(form.dayRate || 0) * Number(form.daysWorked || 0))
const perDiemTotal = computed(() => Number(form.perDiem || 0) * Number(form.daysWorked || 0))
const mileageTotal = computed(() => Number(form.mileage || 0) * 0.67)
const previewGross = computed(
  () => basePay.value + perDiemTotal.value + mileageTotal.value + Number(form.otherExpenses || 0),
)

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
    'Content-Type': 'application/json',
  }
}

function statusClass(status: string) {
  if (status === 'paid') return 'ok'
  if (status === 'pending' || status === 'approved' || status === 'draft') return 'warn'
  return 'muted'
}

function defaultDates() {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - 6)
  form.payPeriodEnd = end.toISOString().slice(0, 10)
  form.payPeriodStart = start.toISOString().slice(0, 10)
  form.payDate = end.toISOString().slice(0, 10)
}

function openPayModal(contractor?: any) {
  defaultDates()
  if (contractor) {
    form.contractorId = String(contractor.id)
    form.contractorName = contractor.name
    form.dayRate = Number(contractor.dayRate || 550)
    form.daysWorked = Number(contractor.daysWorked || 7) || 7
    form.perDiem = Number(contractor.perDiem || 0)
    form.mileage = Number(contractor.mileage || 0)
  } else {
    form.contractorId = ''
    form.contractorName = ''
    form.dayRate = 550
    form.daysWorked = 7
    form.perDiem = 0
    form.mileage = 0
  }
  form.otherExpenses = 0
  form.notes = ''
  showModal.value = true
}

function onContractorChange() {
  const c = contractors.value.find((x) => String(x.id) === form.contractorId)
  form.contractorName = c?.name || ''
  if (c) {
    form.dayRate = Number(c.dayRate || form.dayRate)
  }
}

async function loadAll() {
  loading.value = true
  error.value = ''
  try {
    const [sRes, cRes, hRes] = await Promise.all([
      fetch('/api/company/contractor-payroll-stats', { headers: authHeaders() }),
      fetch('/api/company/payroll-contractors', { headers: authHeaders() }),
      fetch('/api/company/contractor-payroll-history', { headers: authHeaders() }),
    ])
    if (!sRes.ok || !cRes.ok || !hRes.ok) throw new Error('Failed to load contractor payroll')
    stats.value = await sRes.json()
    contractors.value = (await cRes.json()).contractors || []
    history.value = (await hRes.json()).payments || []
  } catch (e: any) {
    error.value = e.message || 'Load failed'
  } finally {
    loading.value = false
  }
}

async function submitPayment() {
  saving.value = true
  error.value = ''
  try {
    const res = await fetch('/api/company/process-contractor-payment', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        contractorId: Number(form.contractorId),
        payPeriodStart: form.payPeriodStart,
        payPeriodEnd: form.payPeriodEnd,
        payDate: form.payDate,
        dayRate: form.dayRate,
        daysWorked: form.daysWorked,
        perDiem: form.perDiem,
        mileage: form.mileage,
        otherExpenses: form.otherExpenses,
        notes: form.notes,
        status: 'pending',
      }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    showModal.value = false
    await loadAll()
  } catch (e: any) {
    error.value = e.message || 'Could not create payment'
  } finally {
    saving.value = false
  }
}

async function markPaid(payment: any) {
  try {
    const res = await fetch('/api/company/mark-contractor-paid', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ id: payment.id }),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    await loadAll()
  } catch (e: any) {
    error.value = e.message || 'Could not mark paid'
  }
}

async function voidPayment(payment: any) {
  if (!window.confirm(`Void payment ${payment.reference}? The record will remain for audit history.`)) return
  try {
    const res = await fetch(`/api/company/void-contractor-payment/${payment.id}`, {
      method: 'POST',
      headers: authHeaders(),
      body: '{}',
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Void failed')
    await loadAll()
  } catch (e: any) {
    error.value = e.message || 'Void failed'
  }
}

onMounted(loadAll)
</script>

<style scoped>
.stat-card {
  @apply bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5;
}
.panel {
  @apply bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6;
}
.form-input {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-emerald-500;
}
.btn-primary {
  @apply px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium disabled:opacity-50;
}
.btn-secondary {
  @apply px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm font-medium;
}
.status-pill {
  @apply px-2 py-1 rounded-full text-xs font-medium;
}
.status-pill.ok {
  @apply bg-emerald-500/20 text-emerald-400;
}
.status-pill.warn {
  @apply bg-amber-500/20 text-amber-400;
}
.status-pill.muted {
  @apply bg-slate-500/20 text-slate-400;
}
.modal-backdrop {
  @apply fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4;
}
.modal {
  @apply w-full max-w-xl bg-slate-800 border border-slate-700 rounded-2xl p-6 shadow-2xl;
}
.field {
  @apply block mb-3;
}
.field span {
  @apply block text-xs text-slate-400 mb-1;
}
.calc-box {
  @apply bg-slate-900/60 border border-slate-700 rounded-xl p-4 text-slate-300 space-y-1;
}
</style>
